import React from 'react';
import { Link } from 'react-router-dom';
import PageSEO from '../components/PageSEO';
import '../styles/ideatoimpact.css';
import useApplicationWindow from '../hooks/useApplicationWindow';

import { CodeIcon, GlobeIcon, DeviceIcon, TrendingUpIcon } from '../components/ServiceIcons';

// ── Data ──────────────────────────────────────────────────────────────────────

const SERVICES = [
  {
    icon: '🏗️',
    title: 'Project Delivery',
    desc: 'We build and execute IT projects using structured teams of experienced professionals and supervised interns. Sprint-based delivery, Agile-native, NDA-protected.',
    cta: 'Engage a Team',
    to: '/services',
  },
  {
    icon: '🎯',
    title: 'Talent Pipeline',
    desc: 'Access a continuous pipeline of pre-assessed, tool-ready IT talent. Our interns arrive role-matched and project-familiar. Zero onboarding friction for your team.',
    cta: 'Hire Interns',
    to: '/program#companies',
  },
  {
    icon: '🏛️',
    title: 'Campus Partnerships',
    desc: 'We partner with engineering colleges to bridge academic learning and industry readiness. Co-certify graduates and improve institutional placement outcomes.',
    cta: 'Partner With Us',
    to: '/program#colleges',
  },
  {
    icon: '💼',
    title: 'Placement Support',
    desc: 'Our graduates get zero-fee placement support through a specialist consultancy. Resume prep, mock interviews, direct company referrals, and direct offers from your internship company.',
    cta: 'About Placement Support',
    to: '/program#placements',
  },
];

const OUR_SERVICES = [
  {
    Icon: CodeIcon,
    color: '#00AEEF',
    title: 'Software Development',
    desc: 'Custom business applications and enterprise software built to fit your exact workflows and scale with you.',
    to: '/services#software-development',
  },
  {
    Icon: GlobeIcon,
    color: '#22C55E',
    title: 'Web Development',
    desc: 'Static and dynamic websites, web apps, and e-commerce platforms - responsive, fast, and SEO-ready.',
    to: '/services#web-development',
  },
  {
    Icon: DeviceIcon,
    color: '#8B5CF6',
    title: 'Mobile App Development',
    desc: 'Native and cross-platform mobile apps for iOS and Android, from MVP to full production launch.',
    to: '/services#mobile-app-development',
  },
  {
    Icon: TrendingUpIcon,
    color: '#F59E0B',
    title: 'Digital Marketing',
    desc: 'SEO, social media, PPC, and content strategies that grow your reach and bring in the right audience.',
    to: '/services#digital-marketing',
  },
];

const HOW_IT_WORKS = [
  {
    step: '01',
    title: 'You Brief Us',
    desc: 'Share your project scope, tech stack, and delivery timeline. We recommend the right team structure.',
  },
  {
    step: '02',
    title: 'We Build the Team',
    desc: 'E.A.T. allocates pre-trained interns - Developer, QA, BA, or Coordinator - matched to your exact requirements.',
  },
  {
    step: '03',
    title: 'We Run the Sprint',
    desc: 'Interns work under E.A.T. mentor supervision. Daily standups, JIRA tracking, and monthly reports keep you in full control.',
  },
  {
    step: '04',
    title: 'You Hire the Best',
    desc: 'Outstanding performers can be directly absorbed at end of engagement - pre-vetted, project-familiar, zero onboarding curve.',
  },
];

// Restore alongside the commented-out testimonials section below when real outcomes are ready
// const OUTCOMES = [
//   {
//     q: "The CampusToCorp intern we engaged came into Sprint 1 already knowing JIRA, Git, and Agile. We've never had a hire who was this productive in week one.",
//     name: 'Tech Lead',
//     role: 'Partner Company',
//     init: 'TL',
//   },
//   {
//     q: 'Our college placement numbers improved significantly after partnering with E.A.T. The students come back with verifiable project experience and interview confidence.',
//     name: 'Placement Officer',
//     role: 'Partner College',
//     init: 'PO',
//   },
//   {
//     q: "I received a Pre-Placement Offer from the partner company at end of Month 6. What E.A.T. built in me over six months is what four years of college couldn't.",
//     name: 'Priya M.',
//     role: 'Business Analyst Intern',
//     init: 'PM',
//   },
// ];

// ── Component ─────────────────────────────────────────────────────────────────

// The IdeaToImpact slide's text and buttons depend on whether applications are open, and on the
// last date, which an admin can change (see hooks/useApplicationWindow.js).
function ideaSlideCopy({ open, label }) {
  return {
    sub: (
      <>
        <strong style={{ color: '#00AEEF', fontWeight: 700 }}>You build. E.A.T. mentors.</strong>{' '}
        100% project sponsorship for approved project resources. No enrollment fee.{' '}
        {open ? `Last date to apply: ${label}.` : 'Applications for the first cohort are closed.'}
      </>
    ),
    primary: open
      ? { label: 'Apply Now →', to: '/ideatoimpact/apply' }
      : { label: 'Explore IdeaToImpact →', to: '/ideatoimpact' },
    secondary: open
      ? { label: 'Explore IdeaToImpact', to: '/ideatoimpact' }
      : { label: 'CampusToCorp Program', to: '/program' },
  };
}

const HERO_SLIDES = [
  {
    headline: (
      <>
        We Build
        <br />
        <span style={{ color: '#00AEEF' }}>IT Teams</span> That
        <br />
        Actually Deliver.
      </>
    ),
    lead: 'E.A.T. is an IT company that designs, staffs, and supervises project teams for businesses, combining experienced professionals with pre-trained interns to execute real deliverables.',
    sub: (
      <>
        Our flagship program,{' '}
        <strong style={{ color: '#00AEEF', fontWeight: 700 }}>CampusToCorp</strong>, is how we build
        the talent that powers these teams.
      </>
    ),
    primary: { label: 'Hire Our Teams →', to: '/services' },
    secondary: { label: 'CampusToCorp Program', to: '/program' },
  },
  {
    headline: (
      <>
        Real Projects.
        <br />
        <span style={{ color: '#00AEEF' }}>Real Delivery.</span>
        <br />
        Real Teams.
      </>
    ),
    lead: 'We do not just train people, we deploy them. Our structured teams pair senior professionals with supervised interns to ship production work under full NDA protection.',
    sub: (
      <>
        Sprint-based, Agile-native delivery with monthly reporting and complete transparency at
        every step.
      </>
    ),
    primary: { label: 'See How We Work →', to: '/services' },
    secondary: { label: 'Our Engagement Model', to: '/program#companies' },
  },
  {
    aside: 'ideatoimpact',
    headline: (
      <>
        Your Idea Could
        <br />
        <span style={{ color: '#00AEEF' }}>Solve a Real</span>
        <br />
        Problem.
      </>
    ),
    lead: 'E.A.T. IdeaToImpact sponsors selected final-year student projects that solve real-world problems through technology. First cohort in Belgaum: 3 projects will be selected.',
  },
  {
    headline: (
      <>
        11+ Years of
        <br />
        <span style={{ color: '#00AEEF' }}>Industry</span>
        <br />
        Experience.
      </>
    ),
    lead: 'Built on over a decade of hands-on IT and corporate experience across global markets, E.A.T. brings proven delivery practices to every engagement.',
    sub: <>120+ clients advised. 250+ professionals trained. A track record you can build on.</>,
    primary: { label: 'About E.A.T. →', to: '/about' },
    secondary: { label: 'Meet the Founder', to: '/founder' },
  },
];

export default function Home() {
  const applyWindow = useApplicationWindow();
  const slides = React.useMemo(
    () =>
      HERO_SLIDES.map((s) =>
        s.aside === 'ideatoimpact' ? { ...s, ...ideaSlideCopy(applyWindow) } : s
      ),
    [applyWindow]
  );
  const [slideIndex, setSlideIndex] = React.useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % HERO_SLIDES.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <PageSEO
        exactTitle
        title="E.A.T.: IT & Software Company in Belgaum, Karnataka"
        description="E.A.T. is an IT company in Belgaum, Karnataka building software, web and mobile apps, and runs CampusToCorp, a 6-month IT internship."
        ogDescription="Need software, a website or a mobile app? E.A.T., an IT company in Belgaum, Karnataka, builds it with Agile, NDA-protected delivery. Tell us what you need built."
        keywords="E.A.T., EAT, EAT Technologies, E.A.T. Technologies, EAT Tech PL, CampusToCorp, Campus to Corp, IT company Belgaum, software company Belgaum, IT companies in Belgaum, software companies in Belgaum, top IT companies in Belgaum, IT companies in Belgaum Karnataka, Belgaum IT companies list, IT companies in Belagavi, software company Belagavi, IT company Bangalore, software company Bangalore, Belgaum, Belagavi, Bangalore, Bengaluru, software development company India, web development company India, mobile app development India, digital marketing agency India, software developer near me, software developer in India, software company in India, software development agency in India, Agile SDLC, Agile software development, Agile development company India, Scrum software development, IT startup India, IT internship Belgaum, IT internship Bangalore, corporate readiness program, final year IT project"
        path="/"
      />
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section
        style={{
          background: 'linear-gradient(135deg, #0A0E1A 0%, #1A1A2E 50%, #0F3460 100%)',
          minHeight: 'calc(100vh - 71px)',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background accents */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(ellipse at 75% 40%, rgba(0,174,239,0.10) 0%, transparent 60%)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'radial-gradient(rgba(0,174,239,0.10) 1px, transparent 1px)',
            backgroundSize: '44px 44px',
            opacity: 0.45,
          }}
        />
        {/* Decorative right-side glow orb */}
        <div
          style={{
            position: 'absolute',
            right: '-120px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: 560,
            height: 560,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(0,174,239,0.08) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />

        <div
          className="container hero-inner"
          style={{
            position: 'relative',
            zIndex: 2,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            paddingTop: 40,
            paddingBottom: 40,
          }}
        >
          {/* Sliding track - overflow:hidden lives here (not on .container) so it clips at
              the content width, not the container's padding box, which would otherwise let
              slivers of adjacent slides leak into the horizontal gutter */}
          <div style={{ overflow: 'hidden' }}>
            <div
              style={{
                display: 'flex',
                transform: `translateX(-${slideIndex * 100}%)`,
                transition: 'transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {slides.map((slide, i) => (
                <div
                  key={i}
                  style={{
                    minWidth: '100%',
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: 40,
                    alignItems: 'center',
                  }}
                  className="hero-grid"
                >
                  {/* Left - text */}
                  <div style={{ maxWidth: 700 }}>
                    <h1
                      style={{
                        fontSize: 'clamp(32px, 4.5vw, 60px)',
                        fontWeight: 900,
                        color: '#fff',
                        lineHeight: 1.06,
                        marginBottom: 20,
                        fontFamily: "'Montserrat', sans-serif",
                      }}
                    >
                      {slide.headline}
                    </h1>

                    <p
                      style={{
                        fontSize: 16,
                        color: 'rgba(255,255,255,0.68)',
                        lineHeight: 1.75,
                        marginBottom: 12,
                        maxWidth: 540,
                      }}
                    >
                      {slide.lead}
                    </p>
                    <p
                      style={{
                        fontSize: 14,
                        color: 'rgba(255,255,255,0.50)',
                        lineHeight: 1.7,
                        marginBottom: 28,
                        maxWidth: 520,
                      }}
                    >
                      {slide.sub}
                    </p>

                    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                      <Link
                        to={slide.primary.to}
                        className="btn-primary"
                        style={{ fontSize: 15, padding: '16px 34px' }}
                      >
                        {slide.primary.label}
                      </Link>
                      <Link
                        to={slide.secondary.to}
                        className="btn-outline"
                        style={{ fontSize: 15, padding: '16px 34px' }}
                      >
                        {slide.secondary.label}
                      </Link>
                    </div>
                  </div>

                  {/* Right - IdeaToImpact philosophy on its slide, founder credentials on the others (hidden via CSS on mobile) */}
                  {slide.aside === 'ideatoimpact' ? (
                    <div
                      className="hero-stats-col"
                      style={{
                        minWidth: 260,
                        borderLeft: '2px solid #00AEEF',
                        padding: '4px 0 4px 24px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                      }}
                    >
                      {[
                        ['Bring the problem.', 'rgba(255,255,255,0.6)'],
                        ['Build the solution.', 'rgba(255,255,255,0.6)'],
                        ['Create the impact.', '#00AEEF'],
                      ].map(([text, color]) => (
                        <div
                          key={text}
                          style={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: 26,
                            fontWeight: 800,
                            lineHeight: 1.25,
                            color,
                          }}
                        >
                          {text}
                        </div>
                      ))}
                      <div
                        style={{
                          marginTop: 10,
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: '1.5px',
                          textTransform: 'uppercase',
                          color: 'rgba(255,255,255,0.5)',
                          fontFamily: "'Montserrat', sans-serif",
                        }}
                      >
                        Belgaum · First cohort
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 200 }}
                      className="hero-stats-col"
                    >
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: '1.5px',
                          textTransform: 'uppercase',
                          color: 'rgba(255,255,255,0.38)',
                          fontFamily: "'Montserrat', sans-serif",
                          paddingLeft: 4,
                          marginBottom: 2,
                        }}
                      >
                        Founder's Track Record
                      </div>
                      {[
                        { num: '11+', label: 'Years in Industry', icon: '🏆' },
                        { num: '120+', label: 'Clients Advised', icon: '🤝' },
                        { num: '30+', label: 'Projects Delivered', icon: '🚀' },
                        { num: '250+', label: 'Professionals Trained', icon: '👥' },
                      ].map((s) => (
                        <div
                          key={s.label}
                          style={{
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid rgba(255,255,255,0.09)',
                            borderRadius: 12,
                            padding: '12px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            backdropFilter: 'blur(8px)',
                          }}
                        >
                          <span style={{ fontSize: 18 }}>{s.icon}</span>
                          <div>
                            <div
                              style={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontSize: 20,
                                fontWeight: 900,
                                color: '#00AEEF',
                                lineHeight: 1,
                              }}
                            >
                              {s.num}
                            </div>
                            <div
                              style={{
                                fontSize: 11,
                                color: 'rgba(255,255,255,0.55)',
                                marginTop: 2,
                                fontWeight: 600,
                              }}
                            >
                              {s.label}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div
                        style={{
                          fontSize: 11,
                          color: 'rgba(255,255,255,0.32)',
                          lineHeight: 1.6,
                          paddingLeft: 4,
                          paddingTop: 2,
                          fontStyle: 'italic',
                        }}
                      >
                        Founded in 2026. Experienced since 2015.
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Slide indicators */}
          <div style={{ display: 'flex', gap: 10, marginTop: 36 }}>
            {HERO_SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setSlideIndex(i)}
                aria-label={`Go to slide ${i + 1}`}
                style={{
                  width: slideIndex === i ? 28 : 10,
                  height: 10,
                  borderRadius: 6,
                  background: slideIndex === i ? '#00AEEF' : 'rgba(255,255,255,0.25)',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  padding: 0,
                }}
              />
            ))}
          </div>
        </div>

        {/* ── Trusted bar pinned to hero bottom ── */}
        <div
          className="trusted-bar"
          style={{
            background: 'rgba(0,174,239,0.15)',
            borderTop: '1px solid rgba(0,174,239,0.25)',
            padding: '13px 0',
            position: 'relative',
            zIndex: 2,
          }}
        >
          <div className="container">
            <p
              style={{
                textAlign: 'center',
                color: '#00AEEF',
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '1.5px',
                textTransform: 'uppercase',
                margin: 0,
              }}
            >
              Agile-Native &nbsp;·&nbsp; NDA-Protected &nbsp;·&nbsp; Experienced-Led,
              Intern-Supported
            </p>
          </div>
        </div>
      </section>

      {/* ── New program: IdeaToImpact ─────────────────────────────────────── */}
      <section className="i2i-strip" aria-label="New program: E.A.T. IdeaToImpact">
        <div className="container">
          <div className="i2i-strip-in">
            <div>
              <span className="i2i-strip-tag">
                New program &nbsp;·&nbsp; Belgaum &nbsp;·&nbsp; First cohort
              </span>
              <p className="i2i-strip-title">
                E.A.T. <span>IdeaToImpact</span>: your idea could solve a real problem.
              </p>
              <p className="i2i-strip-sub">
                E.A.T. is sponsoring 3 selected final-year projects. You build. E.A.T. mentors and
                sponsors.{' '}
                {applyWindow.open
                  ? `Last date to apply: ${applyWindow.label}.`
                  : 'Applications for the first cohort are closed.'}
              </p>
            </div>
            <Link to="/ideatoimpact" className="i2i-strip-link">
              Explore IdeaToImpact →
            </Link>
          </div>
        </div>
      </section>

      {/* ── Our Services ─────────────────────────────────────────────────── */}
      <section className="section-pad" style={{ background: '#F8F9FA' }}>
        <div className="container">
          <div className="section-head">
            <span className="tag">Our Services</span>
            <h2 className="section-title">
              What We <span>Build & Deliver</span>
            </h2>
            <p className="section-sub" style={{ margin: '0 auto' }}>
              Beyond talent development, E.A.T., an IT company in Belgaum, Karnataka, delivers
              hands-on IT services for businesses - executed by experienced professionals and
              supervised interns.
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 20,
            }}
            className="our-services-grid"
          >
            {OUR_SERVICES.map((s) => (
              <Link
                key={s.title}
                to={s.to}
                style={{
                  textDecoration: 'none',
                  background: '#fff',
                  borderRadius: 16,
                  padding: '30px 24px',
                  boxShadow: '0 2px 20px rgba(26,26,46,0.07)',
                  border: '1px solid #E9ECEF',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 10px 40px rgba(0,174,239,0.14)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = '0 2px 20px rgba(26,26,46,0.07)';
                }}
              >
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 14,
                    background: `${s.color}18`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 16,
                  }}
                >
                  <s.Icon size={24} color={s.color} />
                </div>
                <h3
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: 16,
                    fontWeight: 800,
                    color: '#1A1A2E',
                    marginBottom: 10,
                  }}
                >
                  {s.title}
                </h3>
                <p
                  style={{
                    fontSize: 13,
                    color: '#6C757D',
                    lineHeight: 1.7,
                    flex: 1,
                    marginBottom: 16,
                  }}
                >
                  {s.desc}
                </p>
                <span
                  style={{
                    color: '#00AEEF',
                    fontFamily: "'Montserrat', sans-serif",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  Learn More →
                </span>
              </Link>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <Link
              to="/services"
              className="btn-primary"
              style={{ fontSize: 14, padding: '13px 30px' }}
            >
              View All Services →
            </Link>
          </div>
        </div>
      </section>

      {/* ── What We Do ───────────────────────────────────────────────────── */}
      <section className="section-pad">
        <div className="container">
          <div className="section-head">
            <span className="tag">What We Do</span>
            <h2 className="section-title">
              IT Services Built Around <span>Real Delivery</span>
            </h2>
            <p className="section-sub" style={{ margin: '0 auto' }}>
              E.A.T. operates at the intersection of project execution and talent development -
              giving companies the teams they need and graduates the experience that gets them
              hired.
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 24,
              maxWidth: 920,
              margin: '0 auto',
            }}
            className="services-grid"
          >
            {SERVICES.map((s) => (
              <div
                key={s.title}
                style={{
                  background: '#fff',
                  borderRadius: 16,
                  padding: '32px 28px',
                  boxShadow: '0 2px 20px rgba(26,26,46,0.07)',
                  border: '1px solid #E9ECEF',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 10px 40px rgba(0,174,239,0.14)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = '0 2px 20px rgba(26,26,46,0.07)';
                }}
              >
                <div style={{ fontSize: 36, marginBottom: 18 }}>{s.icon}</div>
                <h3
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: 18,
                    fontWeight: 800,
                    color: '#1A1A2E',
                    marginBottom: 12,
                  }}
                >
                  {s.title}
                </h3>
                <p
                  style={{
                    fontSize: 14,
                    color: '#6C757D',
                    lineHeight: 1.76,
                    flex: 1,
                    marginBottom: 24,
                  }}
                >
                  {s.desc}
                </p>
                <Link
                  to={s.to}
                  style={{
                    background: 'transparent',
                    color: '#00AEEF',
                    border: '1.5px solid #00AEEF',
                    borderRadius: 9,
                    fontFamily: "'Montserrat', sans-serif",
                    fontWeight: 700,
                    fontSize: 13,
                    padding: '10px 20px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    alignSelf: 'flex-start',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#00AEEF';
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#00AEEF';
                  }}
                >
                  {s.cta} →
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CampusToCorp Flagship ─────────────────────────────────────────── */}
      <section className="section-pad" style={{ background: '#F8F9FA' }}>
        <div className="container">
          <div
            style={{
              background: 'linear-gradient(135deg, #1A1A2E 0%, #0F3460 100%)',
              borderRadius: 24,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {/* Background grid */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                backgroundImage: 'radial-gradient(rgba(0,174,239,0.10) 1px, transparent 1px)',
                backgroundSize: '36px 36px',
              }}
            />
            <div
              style={{
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                width: '40%',
                background:
                  'radial-gradient(ellipse at right center, rgba(0,174,239,0.08) 0%, transparent 70%)',
              }}
            />

            <div
              style={{
                position: 'relative',
                zIndex: 2,
                padding: '60px 56px',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 60,
                alignItems: 'center',
              }}
              className="flagship-grid"
            >
              {/* Left */}
              <div>
                <div
                  style={{
                    display: 'inline-block',
                    background: 'rgba(0,174,239,0.18)',
                    border: '1px solid rgba(0,174,239,0.35)',
                    color: '#00AEEF',
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 2,
                    padding: '6px 16px',
                    borderRadius: 40,
                    marginBottom: 22,
                    textTransform: 'uppercase',
                    fontFamily: "'Montserrat', sans-serif",
                  }}
                >
                  Flagship Program
                </div>

                <h2
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: 'clamp(28px, 3vw, 40px)',
                    fontWeight: 900,
                    color: '#fff',
                    lineHeight: 1.15,
                    marginBottom: 18,
                  }}
                >
                  CampusToCorp<span style={{ color: '#00AEEF' }}>.</span>
                </h2>
                <p
                  style={{
                    color: 'rgba(255,255,255,0.68)',
                    fontSize: 15,
                    lineHeight: 1.82,
                    marginBottom: 20,
                  }}
                >
                  A 6-month structured program where final-year IT graduates are trained, assessed,
                  and placed into real client projects - under experienced E.A.T. mentors.
                </p>
                <p
                  style={{
                    color: 'rgba(255,255,255,0.50)',
                    fontSize: 14,
                    lineHeight: 1.75,
                    marginBottom: 32,
                  }}
                >
                  CampusToCorp is how E.A.T. builds the talent pipeline that powers our delivery
                  teams. Companies get pre-trained interns. Students get career-defining experience.
                </p>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <Link
                    to="/program"
                    className="btn-primary"
                    style={{ fontSize: 14, padding: '13px 28px' }}
                  >
                    Explore the Program →
                  </Link>
                  <Link
                    to="/register"
                    style={{
                      background: 'transparent',
                      color: 'rgba(255,255,255,0.75)',
                      border: '1.5px solid rgba(255,255,255,0.25)',
                      borderRadius: 9,
                      fontFamily: "'Montserrat', sans-serif",
                      fontWeight: 700,
                      fontSize: 14,
                      padding: '13px 28px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)';
                      e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)';
                      e.currentTarget.style.color = 'rgba(255,255,255,0.75)';
                    }}
                  >
                    Apply as a Student
                  </Link>
                </div>
              </div>

              {/* Right - program stats grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                {[
                  { val: '6', unit: 'Months', sub: 'Hybrid delivery' },
                  { val: '1:20', unit: 'Ratio', sub: 'Mentor-to-intern' },
                  { val: '4', unit: 'Roles', sub: 'Dev · QA · BA · PM' },
                  { val: '3', unit: 'Certificates', sub: 'Industry-issued' },
                ].map((s) => (
                  <div
                    key={s.unit}
                    style={{
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.09)',
                      borderRadius: 14,
                      padding: '22px 20px',
                      textAlign: 'center',
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontSize: 36,
                        fontWeight: 900,
                        color: '#00AEEF',
                        lineHeight: 1,
                      }}
                    >
                      {s.val}
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginTop: 6 }}>
                      {s.unit}
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 3 }}>
                      {s.sub}
                    </div>
                  </div>
                ))}
                <div
                  style={{
                    gridColumn: '1 / -1',
                    background: 'rgba(0,174,239,0.12)',
                    border: '1px solid rgba(0,174,239,0.25)',
                    borderRadius: 14,
                    padding: '18px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 11,
                        color: 'rgba(255,255,255,0.45)',
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                        marginBottom: 4,
                      }}
                    >
                      Cohort Size
                    </div>
                    <div
                      style={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontSize: 22,
                        fontWeight: 900,
                        color: '#00AEEF',
                      }}
                    >
                      100 seats / cycle
                    </div>
                  </div>
                  <div
                    style={{
                      background: '#00AEEF',
                      color: '#fff',
                      borderRadius: 20,
                      fontSize: 11,
                      fontWeight: 700,
                      padding: '5px 14px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Highly Selective
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <style>{`
          @media (max-width: 768px) {
            .flagship-grid { grid-template-columns: 1fr !important; padding: 36px 28px !important; }
          }
        `}</style>
      </section>

      {/* ── How It Works (for companies) ─────────────────────────────────── */}
      <section className="section-pad">
        <div className="container">
          <div className="section-head">
            <span className="tag">Engagement Model</span>
            <h2 className="section-title">
              How We <span>Deliver for You</span>
            </h2>
            <p className="section-sub" style={{ margin: '0 auto' }}>
              From project brief to go-live - E.A.T. manages the team, the sprints, and the quality.
              You own the outcome.
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 0,
              position: 'relative',
            }}
            className="steps-grid"
          >
            {/* Connector line */}
            <div
              style={{
                position: 'absolute',
                top: 36,
                left: '12.5%',
                right: '12.5%',
                height: 2,
                background: 'linear-gradient(90deg, #00AEEF, #0F3460)',
                zIndex: 0,
              }}
              className="steps-line"
            />

            {HOW_IT_WORKS.map((s, i) => (
              <div
                key={s.step}
                style={{ padding: '0 16px', textAlign: 'center', position: 'relative', zIndex: 1 }}
              >
                {/* Step number circle */}
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    background: i === 0 || i === HOW_IT_WORKS.length - 1 ? '#00AEEF' : '#1A1A2E',
                    border: '3px solid #00AEEF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: 18,
                    fontWeight: 900,
                    color: '#fff',
                  }}
                >
                  {s.step}
                </div>
                <h3
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: 15,
                    fontWeight: 800,
                    color: '#1A1A2E',
                    marginBottom: 10,
                  }}
                >
                  {s.title}
                </h3>
                <p style={{ fontSize: 13, color: '#6C757D', lineHeight: 1.72 }}>{s.desc}</p>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: 52 }}>
            <Link
              to="/program#companies"
              className="btn-primary"
              style={{ fontSize: 15, padding: '15px 36px' }}
            >
              See the Full Engagement Model →
            </Link>
          </div>
        </div>
      </section>

      {/* ── Two-audience split ────────────────────────────────────────────── */}
      <section style={{ background: '#F8F9FA', padding: '80px 0' }}>
        <div className="container">
          <div className="section-head">
            <span className="tag">Who We Work With</span>
            <h2 className="section-title">
              E.A.T. Works With <span>Everyone in the Chain</span>
            </h2>
          </div>

          <div
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}
            className="audience-grid"
          >
            {/* Companies */}
            <div
              style={{
                background: '#1A1A2E',
                borderRadius: 20,
                padding: '44px 40px',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: -40,
                  right: -40,
                  width: 180,
                  height: 180,
                  borderRadius: '50%',
                  background: 'rgba(0,174,239,0.07)',
                }}
              />
              <div style={{ position: 'relative', zIndex: 2 }}>
                <div style={{ fontSize: 40, marginBottom: 18 }}>🏢</div>
                <h3
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: 22,
                    fontWeight: 800,
                    color: '#fff',
                    marginBottom: 14,
                  }}
                >
                  For Companies
                </h3>
                <p
                  style={{
                    color: 'rgba(255,255,255,0.60)',
                    fontSize: 14,
                    lineHeight: 1.8,
                    marginBottom: 24,
                  }}
                >
                  Engage pre-trained IT interns for real project delivery. Zero recruitment cost,
                  zero onboarding friction. Full NDA protection. E.A.T. handles all supervision -
                  you define the scope and take the delivery.
                </p>
                <ul
                  style={{
                    listStyle: 'none',
                    marginBottom: 32,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  {[
                    'Zero recruitment cost',
                    'Agile-ready teams from Day 1',
                    'Monthly performance reports',
                    'Direct hiring path for top performers',
                  ].map((i) => (
                    <li
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        color: 'rgba(255,255,255,0.72)',
                        fontSize: 13,
                      }}
                    >
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          background: '#00AEEF',
                          flexShrink: 0,
                        }}
                      />
                      {i}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/program#companies"
                  className="btn-primary"
                  style={{ fontSize: 14, padding: '13px 26px' }}
                >
                  Partner as a Company →
                </Link>
              </div>
            </div>

            {/* Students */}
            <div
              style={{
                background: '#fff',
                border: '1.5px solid #E9ECEF',
                borderRadius: 20,
                padding: '44px 40px',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: -40,
                  right: -40,
                  width: 180,
                  height: 180,
                  borderRadius: '50%',
                  background: 'rgba(0,174,239,0.05)',
                }}
              />
              <div style={{ position: 'relative', zIndex: 2 }}>
                <div style={{ fontSize: 40, marginBottom: 18 }}>🎓</div>
                <h3
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: 22,
                    fontWeight: 800,
                    color: '#1A1A2E',
                    marginBottom: 14,
                  }}
                >
                  For Students
                </h3>
                <p style={{ color: '#6C757D', fontSize: 14, lineHeight: 1.8, marginBottom: 24 }}>
                  Join CampusToCorp and spend 6 months working on real client projects under E.A.T.
                  mentors. Deliver sprints, earn certifications, and launch your career with proof
                  of work - not just a certificate.
                </p>
                <ul
                  style={{
                    listStyle: 'none',
                    marginBottom: 32,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  {[
                    'Real projects for real companies',
                    '1:20 mentor-to-intern ratio',
                    'Triple certification on graduation',
                    'Zero-fee placement support',
                  ].map((i) => (
                    <li
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        color: '#495057',
                        fontSize: 13,
                      }}
                    >
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          background: '#00AEEF',
                          flexShrink: 0,
                        }}
                      />
                      {i}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  className="btn-primary"
                  style={{ fontSize: 14, padding: '13px 26px' }}
                >
                  Apply to CampusToCorp →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Outcomes / Testimonials ───────────────────────────────────────── */}
      {/* TODO: Uncomment when real testimonials are ready
      <section className="section-pad">
        <div className="container">
          <div className="section-head">
            <span className="tag">Outcomes</span>
            <h2 className="section-title">What <span>Our Partners Say</span></h2>
            <p className="section-sub" style={{ margin: '0 auto' }}>
              Results from companies, colleges, and professionals who've worked with E.A.T.
            </p>
          </div>
          <div className="cards-grid">
            {OUTCOMES.map(t => (
              <div key={t.name} className="testi-card">
                <div style={{ fontSize: 44, color: '#00AEEF', lineHeight: 1, fontFamily: 'Georgia, serif', position: 'absolute', top: 18, right: 22, opacity: 0.22 }}>"</div>
                <p style={{ fontSize: 14, color: '#6C757D', lineHeight: 1.80, marginBottom: 20, fontStyle: 'italic' }}>"{t.q}"</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#1A1A2E', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Montserrat', sans-serif", fontSize: 13, fontWeight: 700, color: '#fff' }}>
                    {t.init}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1A1A2E' }}>{t.name}</div>
                    <div style={{ fontSize: 12, color: '#6C757D', marginTop: 2 }}>{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* ── Final CTA ─────────────────────────────────────────────────────── */}
      <section
        style={{
          background: 'linear-gradient(135deg, #1A1A2E 0%, #0F3460 100%)',
          padding: '80px 0',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'radial-gradient(rgba(0,174,239,0.10) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        <div className="container" style={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
          <div
            style={{
              display: 'inline-block',
              background: 'rgba(0,174,239,0.15)',
              border: '1px solid rgba(0,174,239,0.30)',
              color: '#00AEEF',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 2,
              padding: '6px 18px',
              borderRadius: 40,
              marginBottom: 24,
              textTransform: 'uppercase',
              fontFamily: "'Montserrat', sans-serif",
            }}
          >
            Let's Work Together
          </div>
          <h2
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: 'clamp(26px, 3.5vw, 42px)',
              fontWeight: 900,
              color: '#fff',
              marginBottom: 16,
            }}
          >
            Ready to Build With E.A.T.?
          </h2>
          <p
            style={{
              color: 'rgba(255,255,255,0.62)',
              fontSize: 16,
              lineHeight: 1.8,
              maxWidth: 520,
              margin: '0 auto 40px',
            }}
          >
            Whether you're a company looking for a delivery team, a college seeking a placement
            partner, or a student ready for the real world - E.A.T. has a place for you.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link
              to="/services"
              className="btn-primary"
              style={{ fontSize: 15, padding: '16px 36px' }}
            >
              Engage Our Teams →
            </Link>
            <Link
              to="/contact"
              style={{
                textDecoration: 'none',
                display: 'inline-block',
                background: 'transparent',
                color: 'rgba(255,255,255,0.80)',
                border: '1.5px solid rgba(255,255,255,0.28)',
                borderRadius: 10,
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 700,
                fontSize: 15,
                padding: '16px 36px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.65)';
                e.currentTarget.style.color = '#fff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.28)';
                e.currentTarget.style.color = 'rgba(255,255,255,0.80)';
              }}
            >
              Get in Touch
            </Link>
          </div>
        </div>
      </section>

      <style>{`
        .hero-grid { gap: clamp(20px, 3vw, 40px) !important; }
        @media (max-width: 1100px) {
          .hero-stats-col { min-width: 180px !important; }
        }
        @media (max-width: 900px) {
          .hero-grid        { grid-template-columns: 1fr !important; height: auto !important; }
          .hero-stats-col   { display: none !important; }
          .services-grid    { grid-template-columns: 1fr !important; }
          .our-services-grid { grid-template-columns: 1fr 1fr !important; }
          .steps-grid       { grid-template-columns: 1fr 1fr !important; }
          .steps-line       { display: none !important; }
          .audience-grid    { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 600px) {
          .steps-grid       { grid-template-columns: 1fr !important; }
          .our-services-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-height: 700px) {
          .hero-inner        { padding-top: 20px !important; padding-bottom: 20px !important; }
          .hero-grid h1      { font-size: clamp(24px, 4vw, 40px) !important; margin-bottom: 12px !important; }
          .hero-grid p       { margin-bottom: 8px !important; }
          .hero-grid button  { padding: 11px 24px !important; font-size: 13px !important; }
          .trusted-bar       { padding: 8px 0 !important; }
        }
      `}</style>
    </>
  );
}
